<template>
  <div
    ref="pickerRef"
    class="vm-search-select"
    data-vue-managed="true"
    :data-vue-open="open ? 'true' : 'false'"
  >
    <input :name="inputName" type="hidden" class="vm-search-select-value" :value="selectedValue">
    <button
      type="button"
      class="form-control vm-search-select-trigger"
      :aria-expanded="open ? 'true' : 'false'"
      @click.stop="togglePicker"
    >
      <span class="vm-search-select-trigger-label">{{ selectedLabel }}</span>
      <span class="caret"></span>
    </button>
  </div>
  <teleport to="body">
    <div
      v-if="open"
      ref="panelRef"
      class="vm-search-select-panel"
      :style="panelStyle"
      @click.stop
    >
      <input
        v-model="filterKeyword"
        type="text"
        class="form-control vm-search-select-input"
        :placeholder="filterPlaceholder"
      >
      <div class="vm-search-select-options">
        <button
          v-for="option in filteredOptions"
          :key="`os-type-${option.value}`"
          type="button"
          class="vm-search-select-option"
          :class="{ 'is-selected': selectedValue === option.value }"
          @click="selectOption(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  initialValue: {
    type: [String, Number],
    default: ""
  },
  modelValue: {
    type: [String, Number],
    default: undefined
  },
  inputName: {
    type: String,
    required: true
  },
  emptyLabel: {
    type: String,
    default: "Select an OS type"
  },
  filterPlaceholder: {
    type: String,
    default: "Filter OS types"
  }
})
const emit = defineEmits(["update:modelValue"])

const pickerRef = ref(null)
const panelRef = ref(null)
const open = ref(false)
const filterKeyword = ref("")
const selectedValue = ref(String((props.modelValue ?? props.initialValue) || ""))
const pickerId = `os-type-select-${Math.random().toString(36).slice(2)}`
const panelStyle = ref({})

const normalizedOptions = computed(() =>
  (props.options || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const filteredOptions = computed(() => {
  const keyword = filterKeyword.value.trim().toLowerCase()
  if (!keyword) return normalizedOptions.value
  return normalizedOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})

const selectedLabel = computed(() => {
  return normalizedOptions.value.find((option) => option.value === selectedValue.value)?.label || props.emptyLabel
})

watch(() => props.initialValue, (value) => {
  if (props.modelValue !== undefined) {
    return
  }

  selectedValue.value = String(value || "")
})

watch(() => props.modelValue, (value) => {
  if (value === undefined) {
    return
  }

  selectedValue.value = String(value || "")
})

function togglePicker() {
  const nextOpen = !open.value
  if (nextOpen) {
    document.dispatchEvent(new CustomEvent("scc:close-vm-pickers", {
      detail: { sourceId: pickerId }
    }))
  }

  open.value = nextOpen
  if (!open.value) {
    filterKeyword.value = ""
  } else {
    nextTick(() => {
      updatePanelPosition()
    })
  }
}

function selectOption(value) {
  selectedValue.value = String(value)
  emit("update:modelValue", selectedValue.value)
  open.value = false
  filterKeyword.value = ""
}

function closeOnOutsideClick(event) {
  if (pickerRef.value && pickerRef.value.contains(event.target)) {
    return
  }

  if (panelRef.value && panelRef.value.contains(event.target)) {
    return
  }

  if (open.value) {
    open.value = false
    filterKeyword.value = ""
  }
}

function closeOnGlobalEvent(event) {
  if (event.detail && event.detail.sourceId === pickerId) {
    return
  }

  open.value = false
  filterKeyword.value = ""
}

function updatePanelPosition() {
  if (!pickerRef.value) {
    return
  }

  const rect = pickerRef.value.getBoundingClientRect()
  panelStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 6}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`
  }
}

function handleViewportChange() {
  if (!open.value) {
    return
  }

  updatePanelPosition()
}

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick)
  document.addEventListener("scc:close-vm-pickers", closeOnGlobalEvent)
  window.addEventListener("resize", handleViewportChange)
  window.addEventListener("scroll", handleViewportChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
  document.removeEventListener("scc:close-vm-pickers", closeOnGlobalEvent)
  window.removeEventListener("resize", handleViewportChange)
  window.removeEventListener("scroll", handleViewportChange, true)
})
</script>
